import { Icon } from '@/components/primitives/icon';
import {
  isTinted,
  TONE_CLASSES,
  TONE_ICON,
  type Tone,
  toneForStatus,
} from '@/lib/status';
import { cn } from '@/lib/utils';

export interface StatusPillProps {
  /** The server's status word, such as `awaiting_approval`. */
  status?: string;
  /** Overrides the tone the status word reads as. */
  tone?: Tone;
  /** Overrides the shown word (default: the status with spaces). */
  label?: string;
  /** sm: 20 px (tables, default). md: 24 px (sheet heroes). */
  size?: 'sm' | 'md';
  /** A muted detail after the word: "Verified · Margins". */
  secondary?: string;
  className?: string;
}

/**
 * The one status grammar: a colour plus an icon plus a word. Quiet tones
 * (neutral, success) draw the icon in ink and no fill; info, warning and
 * danger are tinted with a one-pixel border.
 */
export function StatusPill({
  status = '',
  tone,
  label,
  size = 'sm',
  secondary,
  className,
}: Readonly<StatusPillProps>) {
  const resolved = tone ?? toneForStatus(status);
  const classes = TONE_CLASSES[resolved];
  const tinted = isTinted(resolved);
  return (
    <span
      data-slot="status-pill"
      data-tone={resolved}
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-sm border px-1.5 text-xs font-medium whitespace-nowrap',
        size === 'md' ? 'h-6' : 'h-5',
        tinted
          ? [classes.tint, classes.border, classes.ink]
          : 'border-transparent text-foreground',
        className,
      )}
    >
      <Icon icon={TONE_ICON[resolved]} size={14} className={classes.ink} />
      <span className="truncate">{label ?? status.replaceAll('_', ' ')}</span>
      {secondary ? (
        <span
          className={cn(
            'truncate font-normal',
            tinted ? null : 'text-muted-foreground',
          )}
        >
          · {secondary}
        </span>
      ) : null}
    </span>
  );
}
